import type { ColumnConfiguration, FieldConfiguration, ListBuilderConfiguration } from '../types';

/**
 * PD-17294 — an item's Price History tab: every recorded change to its list
 * price and costs, newest first (GET /items/:id/price-history, paged on the
 * server). The tab adds the display text (fieldLabel, sourceLabel) to each
 * row. Values leave the decimal count to the tenant's price-precision setting.
 */

const lineBreak = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
});

const field = (
	id: string,
	fieldName: string,
	displayStyle: FieldConfiguration['displayStyle'],
	options: { secondary?: boolean; decimals?: number; dateFormat?: 'date-short' | 'datetime-short' } = {},
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName }, defaultValue: '-' },
	styleConfig: {
		size: options.secondary === true ? 'xs' : 'sm',
		colorRule: {
			type: 'theme',
			themeColor: options.secondary === true ? 'textSecondary' : 'textPrimary',
		},
		formatOptions:
			options.dateFormat != null
				? { dateFormat: options.dateFormat }
				: options.decimals != null
					? { decimalPlaces: options.decimals }
					: undefined,
	},
	displayStyle,
});

const column = (
	id: string,
	headers: Array<string>,
	fields: Array<FieldConfiguration>,
	preset: 'xsmall' | 'small' | 'medium' | 'large' = 'small',
	flexible = false,
): ColumnConfiguration => ({
	id,
	fields,
	headers: headers.map((text, index) => ({ id: `${id}-h${String(index)}`, text })),
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: flexible ? 'flexible' : 'fixed', preset },
	verticalAlignment: 'middle',
});

export const ITEM_PRICE_HISTORY_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'item-price-history-all',
			name: 'All Changes',
			type: 'default',
			active: true,
			cacheTTL: 60000,
			columns: [
				column(
					'item-price-history-date',
					['Changed'],
					[field('item-price-history-date-field', 'changedDate', 'date', { dateFormat: 'datetime-short' })],
				),
				column(
					'item-price-history-field',
					['What Changed'],
					[field('item-price-history-field-field', 'fieldLabel', 'text')],
				),
				column(
					'item-price-history-values',
					['New', '', 'Was'],
					[
						field('item-price-history-new-field', 'newValue', 'currency'),
						lineBreak('item-price-history-lb-1'),
						field('item-price-history-previous-field', 'previousValue', 'currency', { secondary: true }),
					],
				),
				column(
					'item-price-history-change',
					['Change'],
					[field('item-price-history-change-field', 'changePercent', 'percent', { decimals: 1 })],
					'xsmall',
				),
				column(
					'item-price-history-source',
					['Source'],
					[field('item-price-history-source-field', 'sourceLabel', 'text', { secondary: true })],
					'large',
					true,
				),
			],
			searchEnabled: false,
			sortOrder: 0,
		},
	],
	paging: { defaultPageSize: 25, pageSizeOptions: [25, 50, 100] },
	filters: { activeFilters: [], defaultFilters: {} },
};
